import { useQuery } from "react-query";
import { Shield, ShieldAlert } from "lucide-react";
import { apiService } from "../services/api";
import { ProductFindingsResult } from "../types";
import { toItems } from "./ProductThreatModelPanel";

interface ProductSecurityOverviewProps {
  productId: string;
}

type Severity = "Critical" | "High" | "Medium" | "Low";

const SEVERITIES: Severity[] = ["Critical", "High", "Medium", "Low"];

const SEV_BADGE: Record<Severity, string> = {
  Critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  Medium:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Low: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
};

function SummaryTile({
  icon: Icon,
  title,
  counts,
  loading,
  emptyText,
}: {
  icon: typeof Shield;
  title: string;
  counts: Record<Severity, number>;
  loading: boolean;
  emptyText: string;
}) {
  const total = SEVERITIES.reduce((sum, s) => sum + counts[s], 0);
  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      {loading ? (
        <div className="mt-3 h-8 w-16 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
      ) : (
        <>
          <div className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {total}
          </div>
          {total === 0 ? (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {emptyText}
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SEVERITIES.map((sev) => (
                <span
                  key={sev}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SEV_BADGE[sev]} ${counts[sev] === 0 ? "opacity-40" : ""}`}
                >
                  {counts[sev]} {sev}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ProductSecurityOverview({
  productId,
}: ProductSecurityOverviewProps) {
  // pageSize 1: only the unfiltered severity roll-up is needed here.
  const { data: dtResp, isLoading: dtLoading } = useQuery(
    ["product-dt-overview", productId],
    () => apiService.getProductDependencyTrackFindings(productId, 1, 1),
  );
  const dt: ProductFindingsResult | null = (dtResp?.data as any)?.data ?? null;

  // Same key as ProductThreatModelPanel, so react-query shares the request.
  const { data: tmResp, isLoading: tmLoading } = useQuery(
    ["product-threat-models", productId],
    () => apiService.getThreatModels({ product_id: productId }),
  );

  const dtCounts: Record<Severity, number> = {
    Critical: dt?.critical ?? 0,
    High: dt?.high ?? 0,
    Medium: dt?.medium ?? 0,
    Low: dt?.low ?? 0,
  };

  const tmCounts: Record<Severity, number> = {
    Critical: 0,
    High: 0,
    Medium: 0,
    Low: 0,
  };
  for (const tm of toItems(tmResp)) {
    const s = tm.findings_summary?.bySeverity;
    if (!s) continue;
    for (const sev of SEVERITIES) tmCounts[sev] += s[sev] ?? 0;
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Security Overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryTile
          icon={Shield}
          title="Dependency-Track Vulnerabilities"
          counts={dtCounts}
          loading={dtLoading}
          emptyText="No vulnerabilities across linked applications."
        />
        <SummaryTile
          icon={ShieldAlert}
          title="Threat Model Findings"
          counts={tmCounts}
          loading={tmLoading}
          emptyText="No findings from linked threat models."
        />
      </div>
    </div>
  );
}
